import { create } from "zustand";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { PostModel } from "common/models/BaseModel";
import { DateRangePreset, ITopFrequentlyUsedBooksGridFilter } from "../api/types";
import { resolveDateRange } from "./dateRangeHelper";

export interface TopBooksPerformanceStore {
  // ---- Shared page-level date range filter ----
  dateRangePreset: DateRangePreset;
  customFromDate: string | null;
  customToDate: string | null;
  setDateRangePreset: (preset: DateRangePreset) => void;
  setCustomDateRange: (fromDate: string | null, toDate: string | null) => void;
  getResolvedDateRange: () => { fromDate: string; toDate: string };

  // ---- Top Frequently Used Books grid state ----
  topBooksPostModel: PostModel<ITopFrequentlyUsedBooksGridFilter>;
  handleTopBooksFiltering: (
    filterModel: ITopFrequentlyUsedBooksGridFilter,
  ) => void;
  handleTopBooksPagination: (pageModel: GridPaginationModel) => void;
  handleTopBooksSorting: (sortModel: GridSortModel) => void;
}

const initialDateRangePreset: DateRangePreset = "thisYear";
const initialResolvedRange = resolveDateRange(initialDateRangePreset);

const useTopBooksPerformanceStore = create<TopBooksPerformanceStore>()(
  (set, get) => ({
    dateRangePreset: initialDateRangePreset,
    customFromDate: null,
    customToDate: null,
    setDateRangePreset: (preset) => set({ dateRangePreset: preset }),
    setCustomDateRange: (fromDate, toDate) =>
      set({ customFromDate: fromDate, customToDate: toDate }),
    getResolvedDateRange: () => {
      const { dateRangePreset, customFromDate, customToDate } = get();
      return resolveDateRange(dateRangePreset, customFromDate, customToDate);
    },

    topBooksPostModel: {
      pagingModel: {
        page: 0,
        pageSize: 10,
      },
      filterModel: {
        memberTypeID: null,
        materialTypeID: null,
        fromDate: initialResolvedRange.fromDate,
        toDate: initialResolvedRange.toDate,
      },
    },
    handleTopBooksPagination: (pageModel: GridPaginationModel) =>
      set((state) => ({
        topBooksPostModel: {
          ...state.topBooksPostModel,
          pagingModel: { ...pageModel },
        },
      })),
    handleTopBooksFiltering: (filterModel: ITopFrequentlyUsedBooksGridFilter) =>
      set((state) => ({
        topBooksPostModel: {
          ...state.topBooksPostModel,
          filterModel: { ...filterModel },
        },
      })),
    handleTopBooksSorting: (sortModel: GridSortModel) =>
      set((state) => ({
        topBooksPostModel: {
          ...state.topBooksPostModel,
          sortModel: [...sortModel],
        },
      })),
  }),
);

export default useTopBooksPerformanceStore;
